/**
 * 建产品抽屉的表单（添加物料改版 10-30，接口见 main-api `POST /asset-mgr/products/analyze` / `duplicate-check`）。
 * 纯函数，组件只负责渲染。
 * - 分析结果填进人没动过的格子；人改过的（touched）重新分析也不覆盖。
 * - 品牌、型号在规格里（分析接口就这么回）。
 * - 硬检查（挡「建」）：名字非空且 ≤ 200；链接合法；包装数 ≥ 2。
 */
import { isValidLinkUrl } from '@/components/shared/links/LinksEditor'
import type { LinkItem } from '@/types/links'
import type { ProductItemType, ProductTrackingMode } from '@/types/asset-mgr'

export const MAX_NAME_CHARS = 200
/** 分析接口一次最多收几张图 */
export const MAX_ANALYZE_IMAGES = 6
/** 后端上限：analyze text ≤ 500、采购规格名 ≤ 100 */
export const MAX_TEXT_CHARS = 500
export const MAX_PACK_NAME_CHARS = 100
export const I18N_LANGS = ['zh', 'es', 'pt'] as const
export type I18nLang = (typeof I18N_LANGS)[number]
export type I18nText = Partial<Record<I18nLang, string>>

export type ImageKind = 'photo' | 'screenshot'
export interface AnalyzeImage { url: string; kind: ImageKind }

export interface AnalyzeResult {
  name: string
  description?: string | null
  nameI18n?: I18nText | null
  descriptionI18n?: I18nText | null
  specification?: Array<{ key: string; value: string }> | null
  category?: { id: string; name: string; path?: string | null } | null
  unitsPerPack?: number | null
  packName?: string | null
  packPrice?: number | string | null
  price?: number | string | null
  currency?: string | null
  sourceLanguage?: string | null
  links?: Array<{ url: string; vendor?: string | null }> | null
  unavailable?: boolean
}

export interface DuplicateProduct { productId: string; name: string; category?: string | null; confidence?: string | null }
export interface DuplicateResult {
  status: 'matched' | 'ask' | 'none'
  product: DuplicateProduct | null
  candidates: DuplicateProduct[]
  unavailable?: boolean
}

export type Field =
  | 'name' | 'description' | 'categoryId' | 'specification' | 'pack' | 'packName' | 'packPrice' | 'price'
  | `nameI18n.${I18nLang}` | `descriptionI18n.${I18nLang}`

export interface ProductForm {
  text: string
  photos: string[]
  screenshots: string[]
  links: LinkItem[]
  name: string
  nameI18n: I18nText
  description: string
  descriptionI18n: I18nText
  categoryId: string | null
  itemType: ProductItemType
  trackingMode: ProductTrackingMode
  specification: Array<{ key: string; value: string }>
  /** 包装：null = 一个一个买 */
  pack: { units: number; name: string } | null
  /** 一包的价 */
  packPrice: number | null
  /** 一个的价 */
  price: number | null
  /** 分析读出的币种（不是美元时提示人） */
  currency: string | null
  touched: Field[]
  notThese: string[]
}

export const emptyForm = (text = ''): ProductForm => ({
  text, photos: [], screenshots: [], links: [], name: text.trim(), nameI18n: {}, description: '', descriptionI18n: {},
  categoryId: null, itemType: 'CONSUMABLE', trackingMode: 'QUANTITY', specification: [], pack: null,
  packPrice: null, price: null, currency: null, touched: [], notThese: [],
})

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** 人改了某一格：记下，重新分析不再覆盖它 */
export function touch(form: ProductForm, field: Field, patch: Partial<ProductForm>): ProductForm {
  return { ...form, ...patch, touched: form.touched.includes(field) ? form.touched : [...form.touched, field] }
}

/** 分析接口的入参。超过 6 张时截图先送（价格、规格在截图里） */
export function analyzeBody(form: ProductForm) {
  const images: AnalyzeImage[] = [
    ...form.screenshots.map(url => ({ url, kind: 'screenshot' as const })),
    ...form.photos.map(url => ({ url, kind: 'photo' as const })),
  ].slice(0, MAX_ANALYZE_IMAGES)
  const links = form.links.map(l => l.url.trim()).filter(u => u && isValidLinkUrl(u))
  return { text: form.text.trim() || form.name.trim(), images, links }
}

/** 有没有东西可分析（一句话、图或链接） */
export const canAnalyze = (form: ProductForm) => {
  const b = analyzeBody(form)
  return !!b.text || b.images.length > 0 || b.links.length > 0
}

/** 分析结果填进人没动过的格子 */
export function applyAnalysis(form: ProductForm, r: AnalyzeResult): ProductForm {
  const free = (f: Field) => !form.touched.includes(f)
  // AI 读不了：只回了原话当名字；名字还空着就用它，其余人手填
  if (r.unavailable) return free('name') && !form.name.trim() && r.name?.trim() ? { ...form, name: r.name.trim() } : form
  const next: ProductForm = { ...form, nameI18n: { ...form.nameI18n }, descriptionI18n: { ...form.descriptionI18n } }
  if (free('name') && r.name?.trim()) next.name = r.name.trim()
  if (free('description') && r.description != null) next.description = r.description
  for (const lang of I18N_LANGS) {
    const n = r.nameI18n?.[lang]
    if (free(`nameI18n.${lang}`) && n != null) next.nameI18n[lang] = n
    const d = r.descriptionI18n?.[lang]
    if (free(`descriptionI18n.${lang}`) && d != null) next.descriptionI18n[lang] = d
  }
  if (free('categoryId') && r.category?.id) next.categoryId = String(r.category.id)
  if (free('specification') && r.specification) next.specification = r.specification.map(s => ({ key: s.key, value: String(s.value ?? '') }))
  const readPack = r.unitsPerPack && r.unitsPerPack >= 2 ? { units: r.unitsPerPack, name: r.packName?.trim() || '' } : null
  if (free('pack')) next.pack = readPack
  // 份价只配读出来的那个包装；人改过包装数、和读出的不同，就不拿它
  const samePack = !!next.pack && !!readPack && next.pack.units === readPack.units
  if (free('packPrice')) next.packPrice = samePack ? num(r.packPrice) : null
  if (free('price')) next.price = num(r.price) ?? eachFrom(next.pack, next.packPrice)
  if (r.currency) next.currency = r.currency.toUpperCase()
  // 链接：人的留着；分析认出来的供应商名补给同一个链接
  const vendorOf = new Map((r.links ?? []).map(l => [l.url.trim(), l.vendor ?? null]))
  next.links = form.links.map(l => (!l.vendor && vendorOf.get(l.url.trim()) ? { ...l, vendor: vendorOf.get(l.url.trim()) } : l))
  return next
}

const eachFrom = (pack: ProductForm['pack'], packPrice: number | null) =>
  pack && pack.units >= 2 && packPrice != null ? Math.round(packPrice / pack.units * 100) / 100 : null

/** 人改一包的价：一个的价跟着算（人单独改过一个的价就不动） */
export function setPackPrice(form: ProductForm, packPrice: number | null): ProductForm {
  const next = touch(form, 'packPrice', { packPrice })
  if (form.touched.includes('price')) return next
  const each = eachFrom(next.pack, packPrice)
  return each == null ? next : { ...next, price: each }
}

/** 人改包装（个数、名字或去掉）：有份价时一个的价跟着算 */
export function setPack(form: ProductForm, pack: ProductForm['pack']): ProductForm {
  const prev = form.pack
  // 人改了包装名：记下（之后改包装数不再动名字）
  if (pack && prev && pack.name !== prev.name && !form.touched.includes('packName')) form = { ...form, touched: [...form.touched, 'packName'] }
  // 只改了包装数、名字是分析填的（人没写过）：清掉名字，回到「N pack」默认名，免得「8 pack」只装 4 个
  else if (pack && prev && pack.units !== prev.units && !form.touched.includes('packName')) pack = { ...pack, name: '' }
  const next = touch(form, 'pack', pack ? { pack } : { pack: null, packPrice: null })
  if (form.touched.includes('price')) return next
  const each = eachFrom(next.pack, next.packPrice)
  return each == null ? next : { ...next, price: each }
}

export function packProblem(pack: ProductForm['pack']): 'unitsMin' | null {
  if (!pack) return null
  return Number.isInteger(pack.units) && pack.units >= 2 ? null : 'unitsMin'
}

/** 建按钮能不能点；不能时返回原因 key（productCatalog:create.blocked.*） */
export function hardGate(form: ProductForm): string | null {
  const name = form.name.trim()
  if (!name) return 'name.empty'
  if (name.length > MAX_NAME_CHARS) return 'name.tooLong'
  if (form.links.some(l => l.url.trim() && !isValidLinkUrl(l.url))) return 'links'
  if (packProblem(form.pack)) return 'pack'
  if (form.pack && form.pack.name.trim().length > MAX_PACK_NAME_CHARS) return 'packName'
  return null
}

const clean = (o: I18nText): I18nText | null => {
  const out: I18nText = {}
  for (const lang of I18N_LANGS) if (o[lang]?.trim()) out[lang] = o[lang]!.trim()
  return Object.keys(out).length ? out : null
}

/**
 * `POST /asset-mgr/products` 的 body。`personChecked`：人看过分析结果和查重（没有 previewKey），
 * 有建产品权限的人建的只在没分类时挂待审（main-api，添加物料改版）。
 */
export function buildCreatePayload(form: ProductForm) {
  const links = form.links.filter(l => l.url.trim()).map(l => ({ url: l.url.trim(), vendor: l.vendor?.trim() || null, note: l.note?.trim() || null }))
  const spec = form.specification.filter(s => s.key.trim() && s.value.trim())
  return {
    name: form.name.trim(),
    nameI18n: clean(form.nameI18n),
    description: form.description.trim() || null,
    descriptionI18n: clean(form.descriptionI18n),
    categoryId: form.categoryId,
    itemType: form.itemType,
    trackingMode: form.trackingMode,
    price: form.price,
    specification: spec.length ? spec.map(s => ({ key: s.key.trim(), label: s.key.trim(), type: 'text' as const, value: s.value.trim() })) : null,
    // 实物照片在前当主图，截图留在后面
    photos: [...form.photos, ...form.screenshots].length ? [...form.photos, ...form.screenshots] : null,
    links: links.length ? links : null,
    purchaseOption: form.pack ? { units: form.pack.units, name: form.pack.name.trim() || null, unitPrice: form.packPrice } : null,
    personChecked: true,
    isActive: true,
  }
}

/** 建产品 409 同名：拦截器转成 code = CONFLICT_PRODUCT、details.value = 已有产品 id */
export function sameNameProductId(err: unknown): string | null {
  const e = err as { code?: unknown; details?: { field?: unknown; value?: unknown }; response?: { status?: number; data?: { error_code?: unknown; data?: { value?: unknown } } } } | null
  const code = String(e?.code ?? e?.response?.data?.error_code ?? '').toUpperCase()
  if (code !== 'CONFLICT_PRODUCT') return null
  const v = e?.details?.value ?? e?.response?.data?.data?.value
  return v != null && String(v) ? String(v) : null
}
